import React, { useState, useEffect, useRef } from 'react';
import ProjectIntro from './ProjectIntro';
import RoomForm from './RoomForm';
import { saveProjectToDisk, loadProjectFromFile } from './utils/saveProject';

function App() {
  const fileInputRef = useRef(null);
  const [lastFilename, setLastFilename] = useState(null);

  const [projectInfo, setProjectInfo] = useState({
    assessor: '',
    projectName: '',
    projectType: '',
    dateTime: new Date().toISOString(),
    propertyPhoto: null
  });

  const [rooms, setRooms] = useState([
    {
      id: 1,
      room: '',
      area: '',
      observations: '',
      pictures: [],
      labResults: [],
      iaqReadings: null,
      recommendations: '',
      requirements: { requirement: '', approxFt: '' },
      floor: '',
      compassDirection: ''
    }
  ]);

  const [nextRoomId, setNextRoomId] = useState(2);

  useEffect(() => {
    if (rooms.length === 0) {
      setRooms([
        {
          id: 1,
          room: '',
          area: '',
          observations: '',
          pictures: [],
          labResults: [],
          iaqReadings: null,
          recommendations: '',
          requirements: { requirement: '', approxFt: '' },
          floor: '',
          compassDirection: ''
        }
      ]);
      setNextRoomId(2);
    }
  }, [rooms]);

  const handleSaveClick = () => {
    saveProjectToDisk(projectInfo, rooms);
  };

  const handleOpenClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLastFilename(file.name);

    try {
      const loaded = await loadProjectFromFile(file);
      if (!loaded?.projectInfo || !loaded?.rooms) {
        alert('File is missing required fields.');
        return;
      }

      setProjectInfo(loaded.projectInfo);
      setRooms(loaded.rooms);
      setNextRoomId(Math.max(...loaded.rooms.map(r => r.id)) + 1);
    } catch (err) {
      alert('Invalid or corrupt project file.');
      console.error(err);
    }
  };

  const updateProjectInfo = (field, value) => {
    setProjectInfo((prev) => ({ ...prev, [field]: value }));
  };

  const addRoom = () => {
    setRooms((prevRooms) => [
      ...prevRooms,
      {
        id: nextRoomId,
        room: '',
        area: '',
        observations: '',
        pictures: [],
        labResults: [],
        iaqReadings: null,
        recommendations: '',
        requirements: { requirement: '', approxFt: '' },
        floor: '',
        compassDirection: ''
      }
    ]);
    setNextRoomId((id) => id + 1);
  };

  const deleteRoom = (id) => {
    if (rooms.length <= 1) {
      alert('Cannot delete the last remaining room.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete Room ${id}?`)) {
      setRooms((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const updateRoomData = (id, data) => {
    setRooms((prevRooms) =>
      prevRooms.map((r) => (r.id === id ? { ...r, ...data } : r))
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Indoor Environmental Project</h1>

      <div className="mb-6 flex gap-4 items-center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".json.gz"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          className="bg-gray-600 text-white px-4 py-2 rounded"
          onClick={handleOpenClick}
        >
          Open Project
        </button>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={handleSaveClick}
        >
          Save Project
        </button>
      </div>

      <ProjectIntro data={projectInfo} onChange={updateProjectInfo} />

      {rooms.map((room) => (
        <RoomForm
          key={room.id}
          roomData={room}
          onDataChange={updateRoomData}
          onDeleteRoom={deleteRoom}
        />
      ))}

      <div className="mt-6">
        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={addRoom}
        >
          Add Room
        </button>
      </div>
    </div>
  );
}

export default App;
